const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const getToken = () => {
  return localStorage.getItem('oauth_token') || localStorage.getItem('auth_token');
};

const setToken = (token) => {
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
  }
};

const getOAuthToken = () => {
  return localStorage.getItem('oauth_token');
};

const getCurrentUser = () => {
  const userStr = localStorage.getItem('current_user');
  return userStr ? JSON.parse(userStr) : null;
};

const setCurrentUser = (user) => {
  if (user) {
    localStorage.setItem('current_user', JSON.stringify(user));
  } else {
    localStorage.removeItem('current_user');
  }
};

const apiRequest = async (endpoint, options = {}) => {
  const token = options.requiresAuth ? getOAuthToken() || getToken() : getToken();
  const url = `${API_BASE_URL}${endpoint}`;

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  delete config.requiresAuth;

  if (config.body && typeof config.body === 'object') {
    config.body = JSON.stringify(config.body);
  }

  try {
    const response = await fetch(url, config);

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text || 'An error occurred' };
      }
    }

    if (!response.ok) {
      if (response.status === 401) {
        setToken(null);
        setCurrentUser(null);
        if (window.location.pathname.startsWith('/admin')) {
          window.location.reload();
        }
      }
      const error = new Error(data.message || `Error ${response.status}: ${response.statusText}`);
      error.response = { data, status: response.status };
      throw error;
    }

    return data;
  } catch (error) {
    if (error.response || error.name === 'AbortError' || (error.message && error.message.startsWith('Error'))) {
      throw error;
    }
    console.error('API request error:', error);
    throw new Error(error.message || 'Network error. Please check if the server is running.');
  }
};

export const authAPI = {
  login: async (username, password) => {
    const data = await apiRequest('/auth/login', {
      method: 'POST',
      body: { username, password },
    });

    if (data.success && data.token) {
      setToken(data.token);
      setCurrentUser(data.user);
    }

    return data;
  },

  logout: async () => {
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      setToken(null);
      setCurrentUser(null);
    }
  },

  getCurrentUser: async () => {
    const data = await apiRequest('/auth/me');
    if (data.success && data.user) {
      setCurrentUser(data.user);
    }
    return data;
  },
};

export const usersAPI = {
  getAll: () => apiRequest('/users'),
  getById: (id) => apiRequest(`/users/${id}`),
  create: async (userData) => {
    try {
      return await apiRequest('/users', { method: 'POST', body: userData });
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },
  update: (id, userData) => apiRequest(`/users/${id}`, { method: 'PUT', body: userData }),
  delete: (id) => apiRequest(`/users/${id}`, { method: 'DELETE' }),
  getOAuthUsers: () => apiRequest('/users/oauth-users'),
};

export const rolesAPI = {
  getAll: () => apiRequest('/roles'),
  getById: (id) => apiRequest(`/roles/${id}`),
  create: (roleData) => apiRequest('/roles', { method: 'POST', body: roleData }),
  update: (id, roleData) => apiRequest(`/roles/${id}`, { method: 'PUT', body: roleData }),
  delete: (id) => apiRequest(`/roles/${id}`, { method: 'DELETE' }),
};

export const permissionsAPI = {
  getAll: () => apiRequest('/images/permissions'),
};


export const productsAPI = {
  getAll: () => apiRequest('/products'),
  getHot: (days = 30) => apiRequest(`/products/hot?days=${encodeURIComponent(days)}`),
  getById: (id) => apiRequest(`/products/${id}`),
  create: (productData) => apiRequest('/products', { method: 'POST', body: productData }),
  update: (id, productData) => apiRequest(`/products/${id}`, { method: 'PUT', body: productData }),
  delete: (id) => apiRequest(`/products/${id}`, { method: 'DELETE' }),
  clearDiscounts: () => apiRequest('/products/discounts/clear', { method: 'PUT' }),
  reorder: (productIds) => apiRequest('/products/reorder', { method: 'PUT', body: { productIds } }),
  getMods: (productId, texture) => {
    const url = `/products/${productId}/mods${texture ? `?texture=${encodeURIComponent(texture)}` : ''}`;
    return apiRequest(url);
  },
  addMods: (productId, texture, modFiles) =>
    apiRequest(`/products/${productId}/mods`, { method: 'POST', body: { texture, modFiles } }),
  deleteMod: (productId, modId) =>
    apiRequest(`/products/${productId}/mods/${modId}`, { method: 'DELETE' }),
  downloadMod: async (productId, modId) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/products/${productId}/mods/${modId}/download`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Download failed');
    }
    const blob = await response.blob();
    return blob;
  },
};

export const categoriesAPI = {
  getAll: () => apiRequest('/categories'),
  getById: (id) => apiRequest(`/categories/${id}`),
  create: (categoryData) => apiRequest('/categories', { method: 'POST', body: categoryData }),
  update: (id, categoryData) => apiRequest(`/categories/${id}`, { method: 'PUT', body: categoryData }),
  delete: (id) => apiRequest(`/categories/${id}`, { method: 'DELETE' }),
  reorder: (categoryIds) => apiRequest('/categories/reorder', { method: 'PUT', body: { categoryIds } }),
};

export const texturesAPI = {
  getAll: () => apiRequest('/textures'),
  getById: (id) => apiRequest(`/textures/${id}`),
  create: (textureData) => apiRequest('/textures', { method: 'POST', body: textureData }),
  update: (id, textureData) => apiRequest(`/textures/${id}`, { method: 'PUT', body: textureData }),
  delete: (id) => apiRequest(`/textures/${id}`, { method: 'DELETE' }),
};

export const imagesAPI = {
  getLandingImages: () => apiRequest('/images/landing'),
  updateLandingImage: (brand, section, side, imageUrl) =>
    apiRequest('/images/landing', {
      method: 'PUT',
      body: { brand, section, side, imageUrl },
    }),
  uploadLandingImage: async (file) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE_URL}/images/landing/upload`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text || 'An error occurred' };
      }
    }

    if (!response.ok) {
      if (response.status === 401) {
        setToken(null);
        setCurrentUser(null);
        if (window.location.pathname.startsWith('/admin')) {
          window.location.reload();
        }
      }
      throw new Error(data.message || `Error ${response.status}: ${response.statusText}`);
    }

    return data;
  },
};

export const uploadAPI = {
  uploadImage: async (file, scope = 'products') => {
    if (!file) throw new Error('Please select an image file.');
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('File size too large. Maximum size is 5MB.');
    }
    const token = getToken();
    const formData = new FormData();
    formData.append('image', file);

    const query = scope === 'products' ? '' : `?scope=${encodeURIComponent(scope)}`;
    const response = await fetch(`${API_BASE_URL}/upload/image${query}`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text || 'An error occurred' };
      }
    }

    if (!response.ok) {
      if (response.status === 401) {
        setToken(null);
        setCurrentUser(null);
        if (window.location.pathname.startsWith('/admin')) {
          window.location.reload();
        }
      }
      throw new Error(data.message || `Error ${response.status}: ${response.statusText}`);
    }

    const imageUrl = data.data?.path
      ? new URL(data.data.path, window.location.origin).href
      : data.data?.imageUrl || data.data?.url;
    if (!data.success || !imageUrl) {
      throw new Error(data.message || 'The upload did not return an image URL. Please try again.');
    }
    return { ...data, data: { ...data.data, imageUrl } };
  },
  uploadMod: async (file) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('mod', file);

    const response = await fetch(`${API_BASE_URL}/upload/mod`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text || 'An error occurred' };
      }
    }

    if (!response.ok) {
      if (response.status === 401) {
        setToken(null);
        setCurrentUser(null);
        if (window.location.pathname.startsWith('/admin')) {
          window.location.reload();
        }
      }
      throw new Error(data.message || `Error ${response.status}: ${response.statusText}`);
    }

    return data;
  },
};

export const ordersAPI = {
  create: (orderData) => apiRequest('/orders', { method: 'POST', body: orderData }),
  getById: (id) => apiRequest(`/orders/${id}`, { requiresAuth: true }),
  getByOrderId: (orderId) => apiRequest(`/orders/order/${orderId}`),
  getAll: () => apiRequest('/orders', { requiresAuth: true }),
  getAnalytics: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString();
    return apiRequest(`/orders/analytics${query ? `?${query}` : ''}`, { requiresAuth: true });
  },
  getMyOrders: () => apiRequest('/orders/my-orders', { requiresAuth: true }),
  updateStatus: (id, status) => apiRequest(`/orders/${id}/status`, { method: 'PUT', body: { status }, requiresAuth: true }),
  regenerateDownload: (id) => apiRequest(`/orders/my-orders/${id}/regenerate-download`, { method: 'POST', requiresAuth: true }),
  regenerateDownloadAdmin: (id) => apiRequest(`/orders/${id}/regenerate-download`, { method: 'POST', requiresAuth: true }),
  downloadOrderZipAdmin: async (id) => {
    const token = getOAuthToken() || getToken();
    const response = await fetch(`${API_BASE_URL}/orders/${id}/download-zip`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || 'Failed to download order ZIP');
    }
    const disposition = response.headers.get('content-disposition') || '';
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    return {
      blob: await response.blob(),
      filename: filenameMatch ? filenameMatch[1] : `order-${id}.zip`
    };
  },
};

export const paymentsAPI = {
  validateToken: (token, orderId) => apiRequest('/payments/validate', {
    method: 'POST',
    body: { token, orderId }
  }),
};

export const downloadsAPI = {
  getInfo: (token) => apiRequest(`/downloads/${token}`),
  download: (token) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    return `${API_BASE_URL}/downloads/${token}/file`;
  },
};

export const galleriesAPI = {
  getAll: (brand) => {
    const query = brand ? `?brand=${brand}` : '';
    return apiRequest(`/galleries${query}`);
  },
  getById: (id) => apiRequest(`/galleries/${id}`),
  create: (galleryData) => apiRequest('/galleries', { method: 'POST', body: galleryData }),
  update: (id, galleryData) => apiRequest(`/galleries/${id}`, { method: 'PUT', body: galleryData }),
  delete: (id) => apiRequest(`/galleries/${id}`, { method: 'DELETE' }),
  addImage: async (galleryId, file) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE_URL}/galleries/${galleryId}/images`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text || 'An error occurred' };
      }
    }

    if (!response.ok) {
      if (response.status === 401) {
        setToken(null);
        setCurrentUser(null);
        if (window.location.pathname.startsWith('/admin')) {
          window.location.reload();
        }
      }
      throw new Error(data.message || `Error ${response.status}: ${response.statusText}`);
    }

    return data;
  },
  removeImage: (galleryId, imageId) => apiRequest(`/galleries/${galleryId}/images/${imageId}`, { method: 'DELETE' }),
  reorderImages: (galleryId, imageIds) => apiRequest(`/galleries/${galleryId}/images/reorder`, { method: 'PUT', body: { imageIds } }),
  getDownloadUrl: (id) => `${API_BASE_URL}/galleries/${id}/download-zip`,
  getAllDownloadUrl: (brand) => `${API_BASE_URL}/galleries/download-all-zip${brand ? `?brand=${encodeURIComponent(brand)}` : ''}`,
  addImages: async (galleryId, files) => {
    const token = getToken();
    const formData = new FormData();
    files.forEach(file => formData.append('images', file));

    const response = await fetch(`${API_BASE_URL}/galleries/${galleryId}/images/bulk`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      try { data = JSON.parse(text); } catch { data = { message: text || 'An error occurred' }; }
    }

    if (!response.ok) {
      if (response.status === 401) {
        setToken(null);
        setCurrentUser(null);
        if (window.location.pathname.startsWith('/admin')) window.location.reload();
      }
      throw new Error(data.message || `Error ${response.status}: ${response.statusText}`);
    }
    return data;
  },
};

export const partnersAPI = {
  getPublic: () => apiRequest('/partners'),
  getAll: () => apiRequest('/partners/admin', { requiresAuth: true }),
  getPageSettings: () => apiRequest('/partners/page-settings'),
  updatePageSettings: (data) => apiRequest('/partners/page-settings', { method: 'PUT', body: data, requiresAuth: true }),
  create: (partnerData) => apiRequest('/partners', { method: 'POST', body: partnerData, requiresAuth: true }),
  update: (id, partnerData) => apiRequest(`/partners/${id}`, { method: 'PUT', body: partnerData, requiresAuth: true }),
  delete: (id) => apiRequest(`/partners/${id}`, { method: 'DELETE', requiresAuth: true }),
};

export const newsAPI = {
  getPublic: () => apiRequest('/news'),
  getLanding: () => apiRequest('/news/landing'),
  getAll: () => apiRequest('/news/admin', { requiresAuth: true }),
  getConfig: () => apiRequest('/news/config', { requiresAuth: true }),
  updateConfig: (configData) => apiRequest('/news/config', { method: 'PUT', body: configData, requiresAuth: true }),
  updateLandingConfig: (configData) => apiRequest('/news/landing-config', { method: 'PUT', body: configData, requiresAuth: true }),
  create: (articleData) => apiRequest('/news', { method: 'POST', body: articleData, requiresAuth: true }),
  update: (id, articleData) => apiRequest(`/news/${id}`, { method: 'PUT', body: articleData, requiresAuth: true }),
  delete: (id) => apiRequest(`/news/${id}`, { method: 'DELETE', requiresAuth: true }),
};

export const siteSettingsAPI = {
  get: (keys) => apiRequest(`/settings${keys ? `?keys=${keys}` : ''}`),
  update: (data) => apiRequest('/settings', { method: 'PUT', body: data, requiresAuth: true }),
};

export const teamAPI = {
  getPublic: () => apiRequest('/team'),
  getAll: () => apiRequest('/team/admin', { requiresAuth: true }),
  getDepartments: () => apiRequest('/team/departments/admin', { requiresAuth: true }),
  createDepartment: (departmentData) => apiRequest('/team/departments', { method: 'POST', body: departmentData, requiresAuth: true }),
  updateDepartment: (id, departmentData) => apiRequest(`/team/departments/${id}`, { method: 'PUT', body: departmentData, requiresAuth: true }),
  deleteDepartment: (id) => apiRequest(`/team/departments/${id}`, { method: 'DELETE', requiresAuth: true }),
  getBySlug: (slug) => apiRequest(`/team/${encodeURIComponent(slug)}`),
  create: (memberData) => apiRequest('/team', { method: 'POST', body: memberData, requiresAuth: true }),
  update: (id, memberData) => apiRequest(`/team/${id}`, { method: 'PUT', body: memberData, requiresAuth: true }),
  delete: (id) => apiRequest(`/team/${id}`, { method: 'DELETE', requiresAuth: true }),
};

const publicationFormRequest = async (endpoint, method, formData) => {
  const token = getOAuthToken() || getToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: formData,
  });

  const contentType = response.headers.get('content-type');
  const data = contentType?.includes('application/json')
    ? await response.json()
    : { message: await response.text() };

  if (!response.ok) {
    const error = new Error(data.message || `Error ${response.status}: ${response.statusText}`);
    error.response = { data, status: response.status };
    throw error;
  }

  return data;
};

export const publicationsAPI = {
  getPublic: (category) =>
    apiRequest(`/publications${category ? `?category=${encodeURIComponent(category)}` : ''}`),
  getCategories: () => apiRequest('/publications/categories'),
  getBySlug: (slug) => apiRequest(`/publications/${encodeURIComponent(slug)}`),
  recordView: (slug) =>
    apiRequest(`/publications/${encodeURIComponent(slug)}/view`, { method: 'POST' }),
  getAll: () => apiRequest('/publications/admin', { requiresAuth: true }),
  getAdminCategories: () =>
    apiRequest('/publications/categories/admin', { requiresAuth: true }),
  create: (formData) => publicationFormRequest('/publications', 'POST', formData),
  update: (id, formData) => publicationFormRequest(`/publications/${id}`, 'PUT', formData),
  delete: (id) =>
    apiRequest(`/publications/${id}`, { method: 'DELETE', requiresAuth: true }),
  createCategory: (categoryData) =>
    apiRequest('/publications/categories', {
      method: 'POST',
      body: categoryData,
      requiresAuth: true,
    }),
  updateCategory: (id, categoryData) =>
    apiRequest(`/publications/categories/${id}`, {
      method: 'PUT',
      body: categoryData,
      requiresAuth: true,
    }),
  deleteCategory: (id) =>
    apiRequest(`/publications/categories/${id}`, {
      method: 'DELETE',
      requiresAuth: true,
    }),
  getDocumentSource: (slug) => {
    const token = getOAuthToken() || getToken();
    return {
      url: `${API_BASE_URL}/publications/${encodeURIComponent(slug)}/file`,
      ...(token && { httpHeaders: { Authorization: `Bearer ${token}` } }),
    };
  },
  getPage: async (slug, pageNumber) => {
    const token = getOAuthToken() || getToken();
    const response = await fetch(
      `${API_BASE_URL}/publications/${encodeURIComponent(slug)}/pages/${pageNumber}`,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      const data = contentType?.includes('application/json')
        ? await response.json()
        : { message: await response.text() };
      throw new Error(data.message || `Page ${pageNumber} could not be opened.`);
    }

    return response.blob();
  },
  download: async (slug) => {
    const token = getOAuthToken() || getToken();
    const response = await fetch(
      `${API_BASE_URL}/publications/${encodeURIComponent(slug)}/file?download=1`,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      const data = contentType?.includes('application/json')
        ? await response.json()
        : { message: await response.text() };
      throw new Error(data.message || 'Download failed.');
    }

    return response.blob();
  },
};

const staffRosterQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value));
    }
  });
  const value = query.toString();
  return value ? `?${value}` : '';
};

export const staffRosterAPI = {
  getConfig: () => apiRequest('/staff-roster/config', { requiresAuth: true }),
  getMembers: (params) =>
    apiRequest(`/staff-roster/members${staffRosterQuery(params)}`, { requiresAuth: true }),
  createMember: (data) =>
    apiRequest('/staff-roster/members', { method: 'POST', body: data, requiresAuth: true }),
  updateMember: (id, data) =>
    apiRequest(`/staff-roster/members/${id}`, { method: 'PUT', body: data, requiresAuth: true }),
  archiveMember: (id, archive = true) =>
    apiRequest(`/staff-roster/members/${id}/archive`, {
      method: 'PUT',
      body: { archive },
      requiresAuth: true,
    }),
  deleteMember: (id) =>
    apiRequest(`/staff-roster/members/${id}`, { method: 'DELETE', requiresAuth: true }),
  createRank: (data) =>
    apiRequest('/staff-roster/ranks', { method: 'POST', body: data, requiresAuth: true }),
  updateRank: (id, data) =>
    apiRequest(`/staff-roster/ranks/${id}`, { method: 'PUT', body: data, requiresAuth: true }),
  createCircle: (data) =>
    apiRequest('/staff-roster/circles', { method: 'POST', body: data, requiresAuth: true }),
  updateCircle: (id, data) =>
    apiRequest(`/staff-roster/circles/${id}`, { method: 'PUT', body: data, requiresAuth: true }),
  getPending: (includeResolved = false) =>
    apiRequest(`/staff-roster/pending${staffRosterQuery({ includeResolved })}`, { requiresAuth: true }),
  updatePending: (id, data) =>
    apiRequest(`/staff-roster/pending/${id}`, { method: 'PUT', body: data, requiresAuth: true }),
  resolvePending: (id, action, memberId) =>
    apiRequest(`/staff-roster/pending/${id}/resolve`, {
      method: 'POST',
      body: { action, memberId },
      requiresAuth: true,
    }),
  promotePending: (id) =>
    apiRequest(`/staff-roster/pending/${id}/promote`, { method: 'POST', requiresAuth: true }),
  getSummary: () => apiRequest('/staff-roster/summary', { requiresAuth: true }),
  getApprovals: (params) =>
    apiRequest(`/staff-roster/approvals${staffRosterQuery(params)}`, { requiresAuth: true }),
  createApproval: (data) =>
    apiRequest('/staff-roster/approvals', { method: 'POST', body: data, requiresAuth: true }),
  updateApproval: (id, data) =>
    apiRequest(`/staff-roster/approvals/${id}`, { method: 'PUT', body: data, requiresAuth: true }),
  deleteApproval: (id) =>
    apiRequest(`/staff-roster/approvals/${id}`, { method: 'DELETE', requiresAuth: true }),
  getHistory: (limit = 200) =>
    apiRequest(`/staff-roster/history${staffRosterQuery({ limit })}`, { requiresAuth: true }),
};

export const webhooksAPI = {
  getAll: async () => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/webhooks`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || `Error ${response.status}: ${response.statusText}`);
    }
    return data;
  },

  getById: async (id) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/webhooks/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || `Error ${response.status}: ${response.statusText}`);
    }
    return data;
  },

  create: async (webhookData) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/webhooks`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(webhookData)
    });

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text || 'An error occurred' };
      }
    }

    if (!response.ok) {
      const error = new Error(data.message || `Error ${response.status}: ${response.statusText}`);
      error.response = { data, status: response.status };
      throw error;
    }
    return data;
  },

  update: async (id, webhookData) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/webhooks/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(webhookData)
    });

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text || 'An error occurred' };
      }
    }

    if (!response.ok) {
      const error = new Error(data.message || `Error ${response.status}: ${response.statusText}`);
      error.response = { data, status: response.status };
      throw error;
    }
    return data;
  },

  delete: async (id) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/webhooks/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || `Error ${response.status}: ${response.statusText}`);
    }
    return data;
  }
};

export const jobsAPI = {
  getAll: (includeInactive = false) => {
    if (includeInactive) {
      return apiRequest('/jobs/admin?includeInactive=true', { requiresAuth: true });
    }
    return apiRequest('/jobs');
  },
  getById: (id) => apiRequest(`/jobs/${id}`),
  create: (jobData) => apiRequest('/jobs', { method: 'POST', body: jobData, requiresAuth: true }),
  update: (id, jobData) => apiRequest(`/jobs/${id}`, { method: 'PUT', body: jobData, requiresAuth: true }),
  delete: (id) => apiRequest(`/jobs/${id}`, { method: 'DELETE', requiresAuth: true }),
  getCategories: (admin = false) => apiRequest(
    admin ? '/jobs/categories/admin' : '/jobs/categories',
    admin ? { requiresAuth: true } : {}
  ),
  createCategory: (categoryData) => apiRequest('/jobs/categories', {
    method: 'POST', body: categoryData, requiresAuth: true
  }),
  updateCategory: (id, categoryData) => apiRequest(`/jobs/categories/${id}`, {
    method: 'PUT', body: categoryData, requiresAuth: true
  }),
  deleteCategory: (id) => apiRequest(`/jobs/categories/${id}`, {
    method: 'DELETE', requiresAuth: true
  }),

  getApplications: (positionId, status) => {
    const params = new URLSearchParams();
    if (positionId) params.append('positionId', positionId);
    if (status) params.append('status', status);
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest(`/jobs/applications/all${query}`, { requiresAuth: true });
  },
  getApplicationByToken: (token, options = {}) => apiRequest(`/jobs/applications/${token}`, options),
  submitApplication: async (formData) => {
    const token = getToken();
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    const response = await fetch(`${API_BASE_URL}/jobs/applications`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text || 'An error occurred' };
      }
    }

    if (!response.ok) {
      if (response.status === 401) {
        setToken(null);
        setCurrentUser(null);
        if (window.location.pathname.startsWith('/admin')) {
          window.location.reload();
        }
      }
      throw new Error(data.message || `Error ${response.status}: ${response.statusText}`);
    }

    return data;
  },
  updateApplicationStatus: (id, status, notes) =>
    apiRequest(`/jobs/applications/${id}/status`, {
      method: 'PUT',
      body: { status, notes },
      requiresAuth: true
    }),
  deleteApplication: (id) => apiRequest(`/jobs/applications/${id}`, { method: 'DELETE', requiresAuth: true }),
  createApplicationAdmin: (applicationData) => apiRequest('/jobs/applications/admin', {
    method: 'POST',
    body: applicationData,
    requiresAuth: true
  }),
  getMyApplications: (options = {}) => apiRequest('/jobs/applications/my-applications', { ...options, requiresAuth: true }),
};

export const discountCodesAPI = {
  getAll: () => apiRequest('/discount-codes', { requiresAuth: true }),
  getById: (id) => apiRequest(`/discount-codes/${id}`, { requiresAuth: true }),
  validate: (code, total) => apiRequest(`/discount-codes/validate/${code}?total=${total}`),
  create: (data) => apiRequest('/discount-codes', {
    method: 'POST',
    body: data,
    requiresAuth: true
  }),
  update: (id, data) => apiRequest(`/discount-codes/${id}`, {
    method: 'PUT',
    body: data,
    requiresAuth: true
  }),
  delete: (id) => apiRequest(`/discount-codes/${id}`, {
    method: 'DELETE',
    requiresAuth: true
  })
};

export const oauthAPI = {
  getLoginUrl: () => apiRequest('/oauth/login'),
  getMe: () => apiRequest('/oauth/me', { requiresAuth: true }),
  handleCallback: (code) => apiRequest(`/oauth/callback?code=${encodeURIComponent(code)}`),
  selectCharacter: (characterId) => apiRequest('/oauth/select-character', {
    method: 'POST',
    body: { characterId },
    requiresAuth: true
  }),
  getDebugUsers: () => apiRequest('/oauth/debug/users'),
  debugLogin: (userId, characterId) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    return fetch(`${API_BASE_URL}/oauth/debug/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, characterId })
    }).then(async (res) => {
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || `Error ${res.status}`);
      }
      return data;
    });
  },
  updateCharacter: (characterId, email, address, phone, dateOfBirth, residentialAddress, city, routingNumber, portfolio) => apiRequest(`/oauth/character/${characterId}`, {
    method: 'PUT',
    body: { email, address, phone, dateOfBirth, residentialAddress, city, routingNumber, portfolio },
    requiresAuth: true
  }),
};

export { getToken, setToken, getCurrentUser, setCurrentUser };
